import type { KeyboardEvent, MouseEvent } from "react";
import { useState } from "react";

import { Search } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Combobox, IconButton } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { getFaviconUrl } from "app/services/sitesService/sitesUtils";
import { useTranslation } from "pro-features/components/WithTranslation";

import { FIELD_SHELL_HEIGHT } from "../../ui/field-shell/field-shell";
import { Favicon } from "../../ui/layout/favicon";

import type { IDomainSelectProps } from "./domain-input.types";
import { DomainOptionRow } from "./domain-option-row";
import { DomainSelectTrigger, DomainSelectValue } from "./domain-select-styled";
import { useDomainOptions } from "./use-domain-options";

const FAVICON_CONTAINER_SIZE = 24;
// Pulls the popover up over the trigger so its own search input takes the trigger's place instead
// of stacking a second field below it.
const TRIGGER_OVERLAY_OFFSET = -(FIELD_SHELL_HEIGHT + 8);

/**
 * Single-domain picker: autocomplete over the website index plus recent analyses, with free text
 * accepted only once it normalizes to a real domain. `value` is the single source of truth, so the
 * caller can never end up holding a domain the user did not commit.
 */
export const DomainSelect = ({
    value,
    onChange,
    placeholder,
    ariaLabel,
    errorId,
    hasError = false,
    isReadOnly = false,
    width = "100%",
    dataAutomation,
    onOpen,
    onPick,
}: IDomainSelectProps) => {
    const translate = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const { recents, suggestions, customOption, isLoading, isSearching, isEmpty } = useDomainOptions({ searchTerm });

    const resolvedPlaceholder = placeholder ?? translate("monitor_and_protect.domain_search.placeholder");

    const handleOpenChange = (nextIsOpen: boolean) => {
        if (isReadOnly) {
            return;
        }

        setSearchTerm("");
        setIsOpen(nextIsOpen);

        if (nextIsOpen) {
            onOpen?.();
        }
    };

    const handleSelect = (domain: string) => {
        onChange(domain);
        onPick?.(domain);
        setIsOpen(false);
    };

    const handleClear = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onChange("");
    };

    const handleSearchKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Enter" && customOption) {
            handleSelect(customOption.domain);
        }
    };

    const emptyStateText = isSearching
        ? translate("monitor_and_protect.domain_search.empty_state_no_results")
        : translate("monitor_and_protect.domain_search.empty_state_start");

    return (
        <Combobox
            isOpen={isOpen}
            onOpenChange={handleOpenChange}
            matchTriggerWidth
            placement="bottom-start"
            offset={TRIGGER_OVERLAY_OFFSET}
            data-automation={dataAutomation}
        >
            <Combobox.Trigger>
                <DomainSelectTrigger
                    width={width}
                    gap="12px"
                    $hasError={hasError}
                    $isReadOnly={isReadOnly}
                    role="combobox"
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-label={ariaLabel ?? resolvedPlaceholder}
                    aria-describedby={hasError ? errorId : undefined}
                    aria-invalid={hasError}
                    tabIndex={isReadOnly ? -1 : 0}
                    data-automation={`${dataAutomation}-trigger`}
                >
                    {value ? (
                        <Flex
                            alignItems="center"
                            gap="8px"
                            overflow="hidden"
                        >
                            <Favicon
                                iconUrl={getFaviconUrl(value, FAVICON_CONTAINER_SIZE)}
                                alt={value}
                                size={FAVICON_CONTAINER_SIZE}
                                imageSize={16}
                            />
                            <DomainSelectValue
                                fontVariant="input2"
                                color={colorsPalettes.carbon["500"]}
                            >
                                {value}
                            </DomainSelectValue>
                        </Flex>
                    ) : (
                        <Flex
                            alignItems="center"
                            gap="8px"
                        >
                            <Search
                                size="sm"
                                color={colorsPalettes.carbon[200]}
                            />
                            <Text
                                fontVariant="input2"
                                color={colorsPalettes.carbon["200"]}
                            >
                                {resolvedPlaceholder}
                            </Text>
                        </Flex>
                    )}
                    {value && !isReadOnly ? (
                        <IconButton
                            iconName="clear"
                            type="flatDark"
                            iconSize="xs"
                            aria-label={translate("common.clear")}
                            onClick={handleClear}
                            data-automation={`${dataAutomation}-clear`}
                        />
                    ) : null}
                </DomainSelectTrigger>
            </Combobox.Trigger>

            <Combobox.SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                onKeyDown={handleSearchKeyDown}
                placeholder={resolvedPlaceholder}
                isLoading={isLoading}
                icon="globe"
                data-automation={`${dataAutomation}-search`}
            />

            <Combobox.Content emptyState={isEmpty ? emptyStateText : null}>
                {customOption ? (
                    <DomainOptionRow
                        key={customOption.domain}
                        suggestion={customOption}
                        isSelected={customOption.domain === value}
                        onSelect={handleSelect}
                        dataAutomation={dataAutomation}
                    />
                ) : null}
                {suggestions.map((suggestion) => (
                    <DomainOptionRow
                        key={suggestion.domain}
                        suggestion={suggestion}
                        isSelected={suggestion.domain === value}
                        onSelect={handleSelect}
                        dataAutomation={dataAutomation}
                    />
                ))}
                {recents.length ? (
                    <Combobox.Section>
                        <Combobox.SectionHeader>
                            {translate("monitor_and_protect.domain_search.recent_analysis")}
                        </Combobox.SectionHeader>
                        {recents.map((suggestion) => (
                            <DomainOptionRow
                                key={suggestion.domain}
                                suggestion={suggestion}
                                isSelected={suggestion.domain === value}
                                onSelect={handleSelect}
                                dataAutomation={dataAutomation}
                            />
                        ))}
                    </Combobox.Section>
                ) : null}
            </Combobox.Content>
        </Combobox>
    );
};
